import React, { useEffect, useState } from 'react';
import { Form, Switch, Typography, notification } from 'antd';
import {
  Button,
  InformationBox,
  TextArea,
} from '@prio365/prio365-react-library';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { makePrioStyles } from '../../../theme/utils';
import { ProjectId } from '../../../models/Types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiFetchSettings,
  apiSaveProjectSetting,
  apiUpdateProjectSetting,
  apiCreateSetting,
  apiUpdateSetting,
} from '../../settings/api';
import {
  CreateSetting,
  ESettingScope,
  ESettingScopeType,
  SettingKey,
} from '../../../models/Settings';
import { useAccessRights } from '../../users/hooks/useAccessRights';

const useStyles = makePrioStyles((theme) => ({
  root: {
    backgroundColor: theme.old.palette.backgroundPalette.content,
    padding: theme.old.spacing.defaultPadding,
  },
  header: {
    marginBottom: theme.old.spacing.unit(1.5),
  },
  toggleRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.old.spacing.unit(2),
  },
  disabledInfo: {
    fontSize: theme.old.typography.fontSize.small,
    color: theme.old.typography.colors.muted,
    marginTop: theme.old.spacing.unit(1),
  },
}));

export interface AbsenceAutoReplySettingProps {
  scope: ESettingScopeType;
  projectId?: ProjectId;
  className?: string;
  showTitle?: boolean;
}

interface FormValues {
  enabled: boolean;
  note: string;
}

const ABSENCE_SETTING_KEY = 'Project_AllProjectMembersAbsentAutoReply_Setting';

const AbsenceAutoReplySetting: React.FC<AbsenceAutoReplySettingProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const { scope, projectId, className, showTitle = true } = props;
  const isProject = scope === ESettingScope.PROJECT;
  const classes = useStyles();
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const queryClient = useQueryClient();

  const accessRights = useAccessRights(['editProjectAbsenceAutoReply'], {
    projectId: projectId,
  });
  const canEdit = accessRights.editProjectAbsenceAutoReply ?? true;

  const keys: SettingKey[] = isProject
    ? [
        {
          keys: [ABSENCE_SETTING_KEY],
          scopeId: projectId || '',
          eSettingScope: ESettingScope.PROJECT,
        } as SettingKey,
        {
          keys: [ABSENCE_SETTING_KEY],
          scopeId: '',
          eSettingScope: ESettingScope.GLOBAL,
        } as SettingKey,
      ]
    : [
        {
          keys: [ABSENCE_SETTING_KEY],
          scopeId: '',
          eSettingScope: ESettingScope.GLOBAL,
        } as SettingKey,
      ];
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [initialValues, setInitialValues] = useState<FormValues>({
    enabled: false,
    note: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [noteValue, setNoteValue] = useState('');
  const [settingId, setSettingId] = useState<string | null>(null);
  const [fallbackMessage, setFallbackMessage] = useState<string>('');
  const [hasProjectSetting, setHasProjectSetting] = useState<boolean>(false);

  const { data: fetchedSettings, isLoading: loadingSettings } = useQuery({
    queryKey: [scope, projectId, 'setting', ABSENCE_SETTING_KEY],
    queryFn: () => apiFetchSettings(keys),
    staleTime: 1000 * 60 * 5,
  });
  const effectiveFallback =
    !enabled && isProject && !hasProjectSetting ? fallbackMessage : '';
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleValuesChange = () => {
    const current = form.getFieldsValue();
    setNoteValue(current.note || '');
    setIsDirty(
      current.enabled !== initialValues.enabled ||
        current.note !== initialValues.note
    );
  };

  const handleToggleChange = (checked: boolean) => {
    form.setFieldsValue({ enabled: checked });
    setEnabled(checked);
    handleValuesChange();
  };

  const buildSetting = (values: FormValues): CreateSetting => ({
    jsonValue: JSON.stringify({
      isEnabled: values.enabled,
      message: values.note || '',
    }),
    parentSettingId: '',
    relationId: '',
    isDefault: true,
    description: '',
  });

  const handleSave = async () => {
    const values = form.getFieldsValue();
    try {
      setSaving(true);
      if (isProject) {
        if (!projectId) throw new Error('projectId required');
        if (settingId) {
          const { result } = await apiUpdateProjectSetting(
            settingId,
            buildSetting(values)
          );
          if (result.status < 200 || result.status >= 300)
            throw new Error(
              t('projects:projectAbsenceNoteSetting.notification.saveError')
            );
        } else {
          const { result, data } = await apiSaveProjectSetting(
            projectId,
            ABSENCE_SETTING_KEY,
            buildSetting(values)
          );
          if (result.status < 200 || result.status >= 300)
            throw new Error(
              t('projects:projectAbsenceNoteSetting.notification.saveError')
            );
          if (data?.settingId) setSettingId(data.settingId);
        }
      } else {
        if (settingId) {
          const { result } = await apiUpdateSetting(
            settingId,
            buildSetting(values)
          );
          if (result.status < 200 || result.status >= 300)
            throw new Error(
              t('projects:projectAbsenceNoteSetting.notification.saveError')
            );
        } else {
          const { result, data } = await apiCreateSetting(
            buildSetting(values),
            ABSENCE_SETTING_KEY
          );
          if (result.status < 200 || result.status >= 300)
            throw new Error(
              t('projects:projectAbsenceNoteSetting.notification.saveError')
            );
          if (data?.settingId) setSettingId(data.settingId);
        }
      }
      setInitialValues(values);
      setIsDirty(false);
      await queryClient.invalidateQueries({
        queryKey: [scope, projectId, 'setting', ABSENCE_SETTING_KEY],
      });
      notification.open({
        message: t('common:actions.save'),
        description: t(
          'projects:projectAbsenceNoteSetting.notification.savedChanges'
        ),
      });
    } catch (e) {
      notification.open({
        message: t('common:error'),
        description: t(
          'projects:projectAbsenceNoteSetting.notification.saveError'
        ),
      });
    } finally {
      setSaving(false);
    }
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (!fetchedSettings?.data) return;
    const arr = Array.isArray(fetchedSettings.data)
      ? (fetchedSettings.data as any[])
      : [fetchedSettings.data];
    const projectRecord = arr.find((s) => s.scope === ESettingScope.PROJECT);
    const globalRecord = arr.find((s) => s.scope === ESettingScope.GLOBAL);

    if (globalRecord) {
      try {
        const parsed = JSON.parse(globalRecord.jsonValue || '{}');
        if (parsed?.message) setFallbackMessage(parsed.message as string);
      } catch {}
    }
    setHasProjectSetting(!!projectRecord);

    const recordToUse = isProject ? projectRecord : globalRecord;
    if (recordToUse) {
      setSettingId(recordToUse.settingId);
      try {
        const parsed = JSON.parse(recordToUse.jsonValue || '{}');
        const values: FormValues = {
          enabled: !!parsed.isEnabled,
          note: parsed.message || '',
        };
        form.setFieldsValue(values);
        setInitialValues(values);
        setEnabled(values.enabled);
        setNoteValue(values.note);
      } catch {
        form.setFieldsValue({ enabled: false, note: '' });
        setNoteValue('');
      }
    } else {
      form.setFieldsValue({ enabled: false, note: '' });
      setInitialValues({ enabled: false, note: '' });
      setEnabled(false);
      setNoteValue('');
    }
  }, [fetchedSettings, form, isProject]);
  //#endregion
  return (
    <div className={classNames(classes.root, className)}>
      {showTitle && (
        <div className={classes.header}>
          <Typography.Title>
            {t('projects:projectAbsenceNoteSetting.headline')}
          </Typography.Title>
          <InformationBox
            description={t('projects:projectAbsenceNoteSetting.description')}
            marginBottom="regular"
          />
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
      >
        <div className={classes.toggleRow}>
          <Form.Item
            name="enabled"
            valuePropName="checked"
            label={t('projects:projectMailAssignmentSetting.enable')}
          >
            <Switch onChange={handleToggleChange} disabled={!canEdit} />
          </Form.Item>
        </div>
        <Form.Item
          label={t('projects:projectAbsenceNoteSetting.noteLabel')}
          name="note"
          rules={[
            {
              validator: (_, value) => {
                if (enabled && !value) {
                  return Promise.reject(
                    t(
                      'projects:projectAbsenceNoteSetting.validation.noteRequired'
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TextArea
            autoSize={{ minRows: 3, maxRows: 8 }}
            disabled={!enabled || !canEdit}
            placeholder={t(
              'projects:projectAbsenceNoteSetting.notePlaceholder'
            )}
          />
        </Form.Item>
        {effectiveFallback && (
          <div className={classes.disabledInfo}>
            {t('projects:projectAbsenceNoteSetting.usingFallback')}{' '}
            <em>{effectiveFallback}</em>
          </div>
        )}
        <div className={classes.actions}>
          <Button
            disabled={
              !canEdit ||
              !isDirty ||
              saving ||
              loadingSettings ||
              (enabled && !noteValue.trim())
            }
            loading={saving}
            onClick={handleSave}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AbsenceAutoReplySetting;
